import { ReactElement } from 'react';
import reduce from 'lodash/reduce';

import { AppDispatch, GetRootState } from 'App';
import { activeFiltersByTypeSelector, paginationByTypeSelector } from 'store/selectors/listsSelectors';
import Orders from 'api/classes/Orders';
import Products from 'api/classes/Products';
import sendGTM from 'utils/sendGTM';
import sendNotification, { ENotificationTypes } from 'utils/sendNotification';
import { GTM_ACTIVATE_PRODUCTS } from 'utils/constants/gtmConstants';
import {
  EListsActions,
  EListsTypes,
  IBulkUpdateProductsActionProps,
  IGetResultsByTypeActionProps,
  ISetActiveFiltersByTypeActionProps,
  ISetPaginationByTypeActionProps,
  IShopifyCollections,
  IUpdateTableSettingsByTypeActionProps,
} from 'types/listsTypes';

const requestsByType = {
  [EListsTypes.ProductsType]: {
    getResults: Products.getProducts,
  },
  [EListsTypes.OrdersType]: {
    getResults: Orders.getOrders,
  },
};

export const getResultsByTypeAction = ({ listType }: IGetResultsByTypeActionProps) =>
  async(dispatch: AppDispatch, getStore: GetRootState) => {
    const activeFilters = activeFiltersByTypeSelector(getStore(), { listType });
    const { limit, offset } = paginationByTypeSelector(getStore(), { listType });
    const filters = reduce(activeFilters, (acc, { filterKey, value, getValue }) =>
      ({ ...acc, [filterKey]: getValue(value) }), {});

    const { success, data, error }: any = await requestsByType[listType].getResults({ offset, limit, ...filters });

    if (success) {
      dispatch({
        type: EListsActions.GetListResultsByType,
        payload: {
          listType,
          results: data.results,
          pagination: { totalResults: data.totalResults, isLoading: false, isNeedUpdate: false, syncStatus: null },
        },
      });
    } else {
      sendNotification(ENotificationTypes.Error, error);
      dispatch({
        type: EListsActions.SetPaginationByType,
        payload: { listType, pagination: { isLoading: false, isNeedUpdate: false } },
      });
    }
  };

export const setPaginationByTypeAction = (payload: ISetPaginationByTypeActionProps) => ({
  type: EListsActions.SetPaginationByType,
  payload,
});

export const setActiveFiltersByTypeAction = (payload: ISetActiveFiltersByTypeActionProps) => ({
  type: EListsActions.SetActiveFiltersByType,
  payload,
});

export const updateTableSettingsByTypeAction = (payload: IUpdateTableSettingsByTypeActionProps) => ({
  type: EListsActions.UpdateTableSettingsByType,
  payload,
});

// export const saveActiveFiltersByTypeAction = ({ listType, name }: { listType: EListsTypes, name: string }) =>
//   async(dispatch: AppDispatch, getStore: GetRootState) => {
//     const filterJson = activeFiltersByTypeSelector(getStore(), { listType });
//     const { success, data, error }: any = await SavedFilters.saveFilters({ name, filterJson, filterType: 2 });
//
//     if (success) {
//       dispatch({ type: EListsActions.SaveFiltersByType, payload: { listType, savedFilters: data } });
//       sendNotification(ENotificationTypes.Success `${name} filter has been saved`);
//     } else {
//       sendNotification(ENotificationTypes.Error error);
//     }
//   };

export const getProductsTagsAction = () => async(dispatch: AppDispatch) => {
  const { success, data, error }: any = await Products.getTags();

  if (success) {
    dispatch({ type: EListsActions.GetProductsTags, payload: { isUpdated: true, tagsList: data } });
  } else {
    sendNotification(ENotificationTypes.Error, error);
  }
};

export const getProductsShopifyCollectionsAction = () => async(dispatch: AppDispatch) => {
  const { success, data, error }: any = await Products.getShopifyCollections();

  if (success) {
    dispatch({
      type: EListsActions.GetProductsShopifyCollections,
      payload: { isUpdated: true, list: data as IShopifyCollections[] },
    });
  } else {
    sendNotification(ENotificationTypes.Error, error);
  }
};

export const updateProductAction = ({ ids, ...updates }: any) => async(dispatch: AppDispatch) => {
  const id = ids[0];
  const { success, error }: any = await Products.updateProduct({ id }, updates);

  if (success) {
    dispatch({ type: EListsActions.UpdateProduct, payload: { id, updates } });
    sendNotification(ENotificationTypes.Success, 'Updated successfully');

    updates.categoryId && sendGTM({
      ...GTM_ACTIVATE_PRODUCTS,
      product_category: updates.categoryId,
      product_activation_method: 'one by one',
    });
  } else {
    sendNotification(ENotificationTypes.Error, error);
  }
};

const updateRequests = {
  add: Products.bulkAddTags,
  remove: Products.bulkRemoveTags,
  edit: Products.bulkUpdateProduct,
};

const getNotificationProps = (successLength: number, failedLength: number): [ENotificationTypes, ReactElement] => {
  if (!successLength) {
    return [ENotificationTypes.Error, <>Failed: {failedLength} products</>];
  }

  if (!failedLength) {
    return [ENotificationTypes.Success, <>Success: {successLength} products</>];
  }

  return [ENotificationTypes.Warning, <>Success: {successLength} products <br /> Failed: {failedLength} products</>];
};

export const bulkUpdateProductsAction = ({ ids, type, isBulkActivate, ...data }: IBulkUpdateProductsActionProps) =>
  async(dispatch: AppDispatch, getStore: GetRootState) => {
    const { isSelectedAll } = paginationByTypeSelector(getStore(), { listType: EListsTypes.ProductsType });
    const activeFilters = activeFiltersByTypeSelector(getStore(), { listType: EListsTypes.ProductsType });
    const filters = reduce(activeFilters, (acc, { filterKey, value, getValue }) =>
      ({ ...acc, [filterKey]: getValue(value) }), {});

    isBulkActivate && dispatch({ type: EListsActions.IsBulkActivating, payload: { isBulkActivating: true } });
    const {
      success,
      data: resData,
      error,
    }: any = await updateRequests[type]({ updateAll: isSelectedAll, ...filters }, { ids, data });

    if (success) {
      // TODO remove after backend fix response for edit tags
      if (type === 'add' || type === 'remove') {
        dispatch({ type: EListsActions.BulkUpdateProducts, payload: { ids, type, updates: data } });
        sendNotification(ENotificationTypes.Success, 'Updated Successfully');
      } else {
        const { success: successIds, failed: failedIds } = resData.data;

        dispatch({ type: EListsActions.BulkUpdateProducts, payload: { ids: successIds, type, updates: data } });
        sendNotification(...getNotificationProps(successIds.length, failedIds.length));
      }

      data.categoryId && sendGTM({
        ...GTM_ACTIVATE_PRODUCTS,
        product_category: data.categoryId,
        product_activation_method: 'bulk',
      });
    } else {
      sendNotification(ENotificationTypes.Error, error);
    }

    isBulkActivate && dispatch({ type: EListsActions.IsBulkActivating, payload: { isBulkActivating: false } });
  };
